import {
  ExportToExcelButton,
  FilterChipContainer,
  ToggleFiltersButton,
  ToggleFiltersToolbar,
} from '@retail/components';
import {
  SearchOptions,
  useExportSupplierItemsToExcel,
  useMgSupplierItemsSearch,
} from '@retail/products/data-access';
import { MgSupplierItemSearchResult } from '@retail/products/types';
import { Stack, Typography } from '@mui/material';
import { useProductsTFunction } from '@retail/products/i18n';
import { useDisclosure, usePagination } from '@shared/hooks';
import { useSearchFilters } from '@retail/products/context';
import {
  AddItemsMenu,
  MgSupplierItemsTableWithCheckbox,
  ProductSearchFilters,
  TotalItemsLoader,
} from '@retail/products/components';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useMemo } from 'react';
import { TagHeading } from '@shared/components';
import { searchFilters } from '@retail/products/utils';

interface Props {
  includeHeader?: boolean;
  assortmentId: number;
  checkedItems: MgSupplierItemSearchResult[];
  setCheckedItems: (selectedItems: MgSupplierItemSearchResult[]) => void;
  navigateToMgItem?: (mgItemNumber: string) => void;
  onOpenAssortmentItemsModal?: () => void;
  onOpenMonitorItemsModal?: () => void;
  updateItemsIsLoading?: boolean;
}

export function MgSupplierItemSearchContainer({
  includeHeader = false,
  assortmentId,
  checkedItems,
  setCheckedItems,
  navigateToMgItem,
  onOpenAssortmentItemsModal,
  onOpenMonitorItemsModal,
  updateItemsIsLoading,
}: Props) {
  const t = useProductsTFunction();
  const pagination = usePagination();
  const { isBrandContext, appTenant } = useSelectedOrgUnit();
  const showProductOwner = appTenant.hasMainSupplier;

  const { isOpen: showFilters, onToggle: toggleFilters } = useDisclosure(true);

  const { filters, filterOptions, hasActiveFilters } = useSearchFilters();

  const assortmentItemsProps: SearchOptions = {
    size: pagination.pageSize,
    page: pagination.page,
    query: filters.searchQuery ?? '',
    assortmentId,
    withAgreementPrices:
      filters.purchasePrice === 'UNSPECIFIED' ? undefined : filters.purchasePrice === 'YES',
    filterDiscontinued:
      filters.discontinuedItem === 'UNSPECIFIED' ? undefined : filters.discontinuedItem === 'NO',
    inAssortment:
      filters.inAssortment === 'UNSPECIFIED' ? undefined : filters.inAssortment === 'YES',
    fromMainSupplier: (showProductOwner && filters.fromMainSupplier) || undefined,
    supplierIds: filters.supplierNumbers?.map((number: string) => Number(number)),
    groupNumbers: filters.productGroupNumbers,
    discountGroupIds: filters.discountGroupCodes?.map((code: string) => Number(code)),
  };

  const { data: mgSupplierItemPaginatedDto, isFetching } = useMgSupplierItemsSearch(
    assortmentItemsProps,
    {
      enabled: hasActiveFilters,
      suspense: false,
    }
  );

  const supplierItems = MgSupplierItemSearchResult.fromSearchResultDTOs(
    mgSupplierItemPaginatedDto?.result ?? [],
    filterOptions.productGroups
  );
  const totalItems = useMemo(
    () => mgSupplierItemPaginatedDto?.totalCount,
    [mgSupplierItemPaginatedDto?.totalCount]
  );

  const { exportToExcel, isLoading } = useExportSupplierItemsToExcel({
    filters: assortmentItemsProps,
  });

  return (
    <Stack flexGrow={1} overflow="hidden">
      {includeHeader && (
        <Stack flexDirection="row" justifyContent="space-between" whiteSpace="nowrap" px={3}>
          <TagHeading color="primary">
            <Typography variant="h4">{t('products.search.openSearch.label')}</Typography>
          </TagHeading>
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <ExportToExcelButton
              onClick={exportToExcel}
              loading={isLoading}
              disabled={!hasActiveFilters}
            />

            <AddItemsMenu
              onAddItemsToAssortment={onOpenAssortmentItemsModal}
              onAddItemsToMonitoringAssortment={onOpenMonitorItemsModal}
              disabled={!checkedItems.length}
            />
          </Stack>
        </Stack>
      )}
      <ToggleFiltersToolbar sx={{ mb: 1, justifyContent: 'space-between' }}>
        <FilterChipContainer
          allowedKeys={
            showProductOwner
              ? searchFilters
              : searchFilters.filter((key) => key !== 'fromMainSupplier')
          }
          filterOptions={{ ...filterOptions }}
        />

        <Stack flexDirection="row" gap={2} alignItems="flex-end">
          <TotalItemsLoader
            itemsShowing={pagination.pageSize}
            totalItems={totalItems ?? 0}
            isFetching={isFetching}
          />

          <ToggleFiltersButton showFilters={showFilters} toggleFilters={toggleFilters} />
        </Stack>
      </ToggleFiltersToolbar>

      <Stack flexGrow={1} flexDirection="row" height="100%" overflow="auto">
        <MgSupplierItemsTableWithCheckbox
          items={supplierItems ?? []}
          totalItems={mgSupplierItemPaginatedDto?.totalCount ?? 0}
          isLoading={updateItemsIsLoading || isFetching}
          selection={{ selectedItems: checkedItems, selectItems: setCheckedItems }}
          isBrandLevel={isBrandContext}
          pagination={pagination}
          onNavigateToMgItem={navigateToMgItem}
          externalItemSource={appTenant.externalItemSource}
          noActiveFilters={!hasActiveFilters}
          showProductOwner={showProductOwner}
        />

        {showFilters && (
          <ProductSearchFilters
            filterOptions={filterOptions}
            isFetching={isFetching}
            showFilters={showFilters}
            setPage={pagination.setPage}
            showProductOwner={showProductOwner}
          />
        )}
      </Stack>
    </Stack>
  );
}
